import { useState } from "react";
import { toast } from "sonner";
import { Loader2, GraduationCap, ArrowRight, CheckCircle2, ExternalLink, FileDown, ScrollText } from "lucide-react";
import { SCHOOL_PACKAGES, ACARA_V9_URL, IMAGES } from "@/data/content";
import { API, api } from "@/lib/api";
import { OrnamentalDivider } from "@/components/OrnamentalDivider";
import EnquiryForm from "@/components/EnquiryForm";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const ACCENT = {
  burgundy: { bg: "bg-[#6E2638]", text: "text-[#6E2638]", ring: "border-[#6E2638]" },
  forest:   { bg: "bg-[#1E4637]", text: "text-[#1E4637]", ring: "border-[#1E4637]" },
  gold:     { bg: "bg-[#B08B2E]", text: "text-[#8A6A16]", ring: "border-[#D4AF37]" },
};

export default function SchoolTours() {
  return (
    <div data-testid="school-page">
      {/* HERO */}
      <section className="relative h-[46vh] min-h-[360px] overflow-hidden">
        <img src={IMAGES.parliament} alt="Parliament House" className="absolute inset-0 w-full h-full object-cover sepia-soft" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 h-full flex flex-col justify-end pb-12">
          <div className="stamp bg-[#FDFBF7]/85 w-fit">School Programs</div>
          <h1 className="serif text-white text-5xl md:text-6xl mt-4">History that walks off the page.</h1>
          <p className="text-[#F5EFEB] mt-3 max-w-2xl">
            Six curriculum-aligned CBD walking programs, Year 5 through VCE — each tailored to your ACARA V9 achievement standards.
          </p>
        </div>
      </section>

      {/* EXPLAINER */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <div className="stamp">ACARA V9 Aligned</div>
            <h2 className="serif text-3xl md:text-4xl text-[#6E2638] mt-4">
              A walking classroom. A curriculum you already teach.
            </h2>
            <p className="mt-4 text-[#1A1817]/85 leading-relaxed">
              Every program is <b>directly tailored to your curriculum and content needs</b>, integrating the specific{" "}
              <a href={ACARA_V9_URL} target="_blank" rel="noreferrer"
                 className="text-[#6E2638] underline decoration-[#D4AF37] underline-offset-4 inline-flex items-center gap-1">
                ACARA V9 achievement standards <ExternalLink size={12} />
              </a>{" "}
              you'd like Martin to focus on. Tell us your unit outline, learning intentions or assessment task —
              Martin builds the walk around them.
            </p>
          </div>
          <div className="card-warm rounded-2xl p-6">
            <ScrollText className="text-[#6E2638]" />
            <div className="serif text-2xl text-[#6E2638] mt-2">Every program includes</div>
            <ul className="mt-3 space-y-2 text-sm text-[#1A1817]/85">
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#1E4637] shrink-0 mt-0.5" /> 2-hour guided CBD walk</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#1E4637] shrink-0 mt-0.5" /> Up to ~50 students per cohort</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#1E4637] shrink-0 mt-0.5" /> Pre-tour teacher brief</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#1E4637] shrink-0 mt-0.5" /> Student worksheet</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#1E4637] shrink-0 mt-0.5" /> Weekday morning start times</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#1E4637] shrink-0 mt-0.5" /> Tax invoice for school records</li>
            </ul>
          </div>
        </div>
      </section>

      {/* PACKAGE GRID */}
      <section className="bg-[#F5EFEB] border-y border-[#E5D9C5] py-16" data-testid="school-packages">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <OrnamentalDivider label="Choose a program" />
          <div className="grid md:grid-cols-2 gap-6 mt-10">
            {SCHOOL_PACKAGES.map((p) => (
              <PackageCard key={p.id} pkg={p} />
            ))}
          </div>
        </div>
      </section>

      {/* LOGISTICS + ENQUIRY */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <div className="stamp">Not ready to book?</div>
          <h2 className="serif text-3xl text-[#6E2638] mt-4">Enquire instead.</h2>
          <p className="mt-4 text-[#1A1817]/85 leading-relaxed">
            Prefer a chat before committing? Send an enquiry — Martin will personally respond
            with dates, program tailoring options, and answer any curriculum questions your Head of
            Faculty needs before signing off.
          </p>
          <a href={`${API}/brochure/school`} className="btn-outline-gold mt-8 inline-flex" data-testid="brochure-school">
            <FileDown size={16} /> Download PDF brochure
          </a>
        </div>
        <div className="lg:col-span-3">
          <EnquiryForm tourType="school" />
        </div>
      </section>
    </div>
  );
}

function PackageCard({ pkg }) {
  const acc = ACCENT[pkg.accent] || ACCENT.burgundy;
  const [open, setOpen] = useState(false);

  return (
    <div className={`card-warm rounded-2xl p-7 flex flex-col ${acc.ring} border-l-4`} data-testid={`school-package-${pkg.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className={`inline-block text-[10px] font-bold uppercase tracking-[0.24em] px-3 py-1 rounded-full ${acc.bg} text-[#FDFBF7]`}>
            {pkg.year}
          </div>
          <h3 className="serif text-2xl md:text-3xl text-[#6E2638] mt-3 leading-snug">{pkg.title}</h3>
          <p className="text-sm mt-1 italic text-[#1A1817]/70">{pkg.tagline}</p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-widest text-[#1E4637]">flat rate</div>
          <div className="serif text-3xl text-[#6E2638]">${599}</div>
          <div className="text-[10px] text-[#1A1817]/60">AUD, per program</div>
        </div>
      </div>

      <p className="mt-4 text-[15px] text-[#1A1817]/85 leading-relaxed flex-1">{pkg.description}</p>

      <div className="mt-5 rounded-xl bg-[#FDFBF7] border border-[#E5D9C5] p-4">
        <div className="text-[10px] uppercase tracking-[0.24em] text-[#1E4637]">Curriculum</div>
        <div className={`serif text-lg ${acc.text} mt-1`}>{pkg.curriculum.subject}</div>
        <div className="text-xs text-[#1A1817]/75">{pkg.curriculum.strand}</div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {pkg.curriculum.standards.map((s) => (
            <span key={s} className="text-[10px] font-mono px-2 py-0.5 bg-[#F5EFEB] border border-[#E5D9C5] rounded">
              {s}
            </span>
          ))}
        </div>
        <p className="text-[11px] mt-3 italic text-[#1A1817]/70">
          Martin tailors this program directly to your curriculum and content needs, integrating the specific ACARA V9 achievement standards you nominate.
        </p>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <button className="btn-primary mt-6 w-full justify-center" data-testid={`book-${pkg.id}`}>
            Book this program <ArrowRight size={16} />
          </button>
        </DialogTrigger>
        <BookingDialog pkg={pkg} onClose={() => setOpen(false)} />
      </Dialog>
    </div>
  );
}

function BookingDialog({ pkg, onClose }) {
  const [form, setForm] = useState({
    school_name: "", contact_name: "", contact_email: "", contact_phone: "",
    preferred_date: "", student_count: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.school_name || !form.contact_name || !form.contact_email) {
      toast.error("School, contact name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        package_id: pkg.id,
        school_name: form.school_name,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone || null,
        preferred_date: form.preferred_date || null,
        student_count: form.student_count ? Number(form.student_count) : null,
        notes: form.notes || null,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/schools/bookings", payload);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error("Payment setup didn't return a checkout URL.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent className="max-w-lg" data-testid={`booking-dialog-${pkg.id}`}>
      <DialogHeader>
        <DialogTitle className="serif text-2xl text-[#6E2638]">
          Book · {pkg.year}: {pkg.title}
        </DialogTitle>
        <DialogDescription>
          Flat rate <b>AUD $599</b> · Up to ~50 students · Martin tailors to your ACARA V9 standards.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4 mt-2">
        <div>
          <Label htmlFor={`s-${pkg.id}-school`}>School name *</Label>
          <Input id={`s-${pkg.id}-school`} data-testid={`sch-name-${pkg.id}`} value={form.school_name}
            onChange={(e) => update("school_name", e.target.value)} required />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor={`s-${pkg.id}-name`}>Your name *</Label>
            <Input id={`s-${pkg.id}-name`} data-testid={`sch-contact-${pkg.id}`} value={form.contact_name}
              onChange={(e) => update("contact_name", e.target.value)} required />
          </div>
          <div>
            <Label htmlFor={`s-${pkg.id}-email`}>Email *</Label>
            <Input id={`s-${pkg.id}-email`} type="email" data-testid={`sch-email-${pkg.id}`} value={form.contact_email}
              onChange={(e) => update("contact_email", e.target.value)} required />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor={`s-${pkg.id}-phone`}>Phone (optional)</Label>
            <Input id={`s-${pkg.id}-phone`} value={form.contact_phone}
              onChange={(e) => update("contact_phone", e.target.value)} />
          </div>
          <div>
            <Label htmlFor={`s-${pkg.id}-count`}>Approx. students</Label>
            <Input id={`s-${pkg.id}-count`} type="number" min={1} max={200} value={form.student_count}
              onChange={(e) => update("student_count", e.target.value)} placeholder="e.g. 45" />
          </div>
        </div>
        <div>
          <Label htmlFor={`s-${pkg.id}-date`}>Preferred date (Martin will confirm)</Label>
          <Input id={`s-${pkg.id}-date`} type="date" value={form.preferred_date}
            onChange={(e) => update("preferred_date", e.target.value)} />
        </div>
        <div>
          <Label htmlFor={`s-${pkg.id}-notes`}>Curriculum notes / achievement standards to focus on</Label>
          <Textarea id={`s-${pkg.id}-notes`} rows={3} maxLength={500}
            placeholder="e.g. AC9HS6K01 focus, our unit ends with a source-analysis SAC on 3 June…"
            value={form.notes} onChange={(e) => update("notes", e.target.value)} />
        </div>
        <div className="rounded-xl bg-[#F5EFEB] border border-[#E5D9C5] p-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-[#1E4637]">You'll pay</div>
            <div className="serif text-2xl text-[#6E2638]" data-testid={`total-${pkg.id}`}>AUD $599.00</div>
            <div className="text-xs text-[#1A1817]/70 mt-1">Secured via Stripe · Tax invoice included</div>
          </div>
          <GraduationCap className="text-[#6E2638] opacity-70" size={32} />
        </div>
        <button type="submit" disabled={submitting} className="btn-primary w-full justify-center" data-testid={`submit-${pkg.id}`}>
          {submitting ? <><Loader2 size={16} className="animate-spin" /> Redirecting to secure checkout…</> :
            <>Continue to payment <ArrowRight size={16} /></>}
        </button>
      </form>
    </DialogContent>
  );
}
